// The chat bubble is the KraftWare chat widget's own button (cs.kraftware.dev/widget.js), back in its corner
// (kraftware-dev#120). This module makes it demo-aware without changing the widget:
//  - visitorContext(): what the visitor is looking at and doing — chapter, alarms, the recent detected-event queue and
//    their own actions — packed into the 600 characters the widget sends with every message. The host republishes it
//    on every change, so whoever opens the chat shares that queue with the agent: opening it is the interest.
//  - cues: when a visitor shows interest (a fault they injected was detected, they open event after event, they finish
//    the tour), a small prompt appears by the chat button. It never opens the chat by itself; "Ask" does, with a greeting
//    about what they just did. At most a couple per visit, never while narration plays.
//  - open({reason}): the hook a future inference trigger calls to bring the chat up.

/** The widget sends at most this much visitor context (widget.js slices __xlVisitorContext to 600). */
export const CONTEXT_MAX = 600

const clip = (s, n) => { s = String(s || "").replace(/\s+/g, " ").trim().replace(/\.$/, ""); return s.length > n ? s.slice(0, n - 1) + "…" : s }

/** Plant-time age, compact: "now", "40s ago", "12m ago", "2h ago". */
export function ago(sec) {
  sec = Math.max(0, Math.round(Number(sec) || 0))
  if (sec < 5) return "now"
  if (sec < 60) return `${sec}s ago`
  if (sec < 3600) return `${Math.round(sec / 60)}m ago`
  return `${Math.round(sec / 3600)}h ago`
}

/**
 * The visitor context, newest-first where it matters, never over CONTEXT_MAX. Order of priority: where they are, what is
 * alarming, what they did, then as many recent detected events as fit.
 * @param events  detected events (oldest first), each { class, interpretation, t0, unit }
 * @param actions the visitor's own recent actions, oldest first (plain phrases)
 */
export function visitorContext({ chapterTitle = "", alarms = [], events = [], actions = [], now = 0 } = {}) {
  const where = chapterTitle ? `on tour chapter "${clip(chapterTitle, 60)}"` : "exploring freely"
  const parts = [`EventDetect batching kitchen demo (kraftware.dev/demos/batch-lifecycle), visitor ${where}.`]
  parts.push(alarms.length ? `Alarms: ${alarms.slice(0, 3).map((a) => clip(a, 60)).join("; ")}.` : "No alarms.")
  if (actions.length) parts.push(`Visitor did: ${actions.slice(-3).map((a) => clip(a, 50)).join("; ")}.`)
  let out = parts.join(" ")
  const recent = events.slice(-6).reverse()
  if (recent.length) {
    let q = " Detected (newest first):"
    for (const e of recent) {
      const item = ` ${clip(e.interpretation || e.class, 70)} (${ago(now - (e.t0 || 0))});`
      if ((out + q + item).length > CONTEXT_MAX) break
      q += item
    }
    if (q.endsWith(";")) out += q.slice(0, -1) + "."
  }
  return out.length > CONTEXT_MAX ? out.slice(0, CONTEXT_MAX - 1) + "…" : out
}

/** What each cue says when the visitor taps Ask, and the replies it offers. */
export const CUES = {
  fault: (d) => ({
    prompt: `Saw the ${d.fault} you injected get detected. Talk it through?`,
    greeting: `I watched the ${d.fault} you injected become a detected event: "${clip(d.interpretation, 120)}". Want me to walk through how it was detected and what the cause chain says?`,
    replies: ["Walk me through the cause chain", "How would this look on our equipment?", "I'd like to talk to an engineer"],
  }),
  events: () => ({
    prompt: "Digging into the events? Ask about any of them.",
    greeting: "You're looking through the detected events. Ask me about any of them, or how the detector decides what counts as an event.",
    replies: ["How does the detector decide?", "Explain the newest event", "I'd like to talk to an engineer"],
  }),
  end: () => ({
    prompt: "Finished the tour. How would this fit your plant?",
    greeting: "You've finished the tour. How would this map onto your plant? Tell me what you run and I'll sketch where the events would come from.",
    replies: ["Map this onto our equipment", "Add me to the demo & hardware list", "I'd like to talk to an engineer"],
  }),
}

const FAULT_NAMES = { sticky_valve: "sticky valve", cavitation: "mixer cavitation", loadcell_drift: "load cell drift", tank_high: "tank fill" }
/** The equipment each fault acts on (sim.js injectFault): only a detection there is "your fault, detected". */
export const FAULT_UNITS = { sticky_valve: "ST-102", cavitation: "ST-103", loadcell_drift: "ST-105", tank_high: "TK-200" }

/**
 * @param doc, win   the page; win.xlcsOpen is the widget's API (absent until widget.js loads, or if it never does)
 * @param context    () => the visitor context string (visitorContext above)
 * @param quiet      () => true while a cue must wait (narration playing, a modal open)
 * @param clock      () => ms (Date.now); the spacing rules use it
 */
export function createChatCues({ doc, win, context = () => "", quiet = () => false, clock = () => Date.now(),
  firstAfterMs = 30000, minGapMs = 45000, maxCues = 2, hideAfterMs = 20000, faultWindowMs = 180000 } = {}) {
  const born = clock()
  const shown = new Set()
  let count = 0, lastAt = -Infinity, hideTimer = null
  let pendingFault = null              // { fault, at }: a fault the visitor injected, waiting for its detection
  const opened = []                    // times the visitor opened an event card

  const el = doc.createElement("div")
  el.className = "chat-cue"; el.setAttribute("role", "status"); el.hidden = true
  const text = doc.createElement("span"); text.className = "chat-cue-text"
  const ask = doc.createElement("button"); ask.type = "button"; ask.className = "chat-cue-ask"; ask.textContent = "Ask"
  const x = doc.createElement("button"); x.type = "button"; x.className = "chat-cue-x"; x.setAttribute("aria-label", "Dismiss"); x.textContent = "✕"
  el.append(text, ask, x)
  doc.body.appendChild(el)
  let current = null
  ask.addEventListener("click", () => { const c = current; hide(); if (c) open({ reason: c.reason, greeting: c.greeting, replies: c.replies }) })
  x.addEventListener("click", hide)

  const ready = () => typeof win.xlcsOpen === "function"
  const chatOpen = () => { const b = doc.getElementById("xlcs-box"); return !!(b && b.classList.contains("open")) }
  function hide() { el.hidden = true; current = null; if (hideTimer) { win.clearTimeout(hideTimer); hideTimer = null } }

  /** Show the cue for `reason` if the spacing rules allow it. Returns whether it showed. */
  function cue(reason, data = {}) {
    const now = clock()
    if (!ready() || chatOpen() || quiet() || shown.has(reason) || count >= maxCues) return false
    if (now - born < firstAfterMs || now - lastAt < minGapMs) return false
    const c = CUES[reason](data)
    current = { reason, ...c }
    shown.add(reason); count++; lastAt = now
    text.textContent = c.prompt
    el.hidden = false
    hideTimer = win.setTimeout(hide, hideAfterMs)
    return true
  }

  /** Open the chat with the live context (and, from a cue, a greeting about what the visitor just did). */
  function open({ reason = "visitor", greeting, replies } = {}) {
    if (!ready()) return false
    hide()
    win.xlcsOpen({ visitorContext: context(), customGreeting: greeting, quickReplies: replies })
    return true
  }

  return {
    el, cue, open, hide,
    /** The host reports what happened; the rules decide whether it is interest. */
    note(kind, data = {}) {
      const now = clock()
      if (kind === "fault") pendingFault = { fault: FAULT_NAMES[data.fault] || String(data.fault || "fault").replace(/_/g, " "), unit: FAULT_UNITS[data.fault] || null, at: now }
      else if (kind === "detected" && pendingFault && now - pendingFault.at <= faultWindowMs
        && data.class !== "batch.complete" && (!pendingFault.unit || data.unit === pendingFault.unit)) {   // the fault's own equipment
        if (cue("fault", { fault: pendingFault.fault, interpretation: data.interpretation })) pendingFault = null
      } else if (kind === "eventOpened") {
        opened.push(now)
        while (opened.length && now - opened[0] > faultWindowMs) opened.shift()
        if (opened.length >= 2) cue("events")
      } else if (kind === "tourEnd") cue("end")
    },
    destroy() { hide(); el.remove() },
  }
}
